import { athenaAuthConfig } from "../../../auth/config.ts";
import { assertLocalAuthHooks } from "../../../auth/hooks/assert-local.ts";
import { assertLocalAuthObservability } from "../../../auth/observability/config.ts";
import {
  type AthenaClient,
  type AthenaClientConfig,
  createClientWithNormalizer,
  normalizeUniversalCreateClientConfig,
} from "../../../client/create-client.ts";
import { AthenaConfigurationError } from "../../../config/errors.ts";
import { getAthenaClientInternals } from "../../../runtime/client-internals.ts";
import { assertDataLifecycleConfig } from "../../../runtime/data/lifecycle/assert.ts";
import { createAthenaRuntimeReadiness } from "../../../runtime/readiness/index.ts";
import { resolveDatabaseUri } from "../../../runtime/resolve.ts";
import type { AthenaBillingConfig } from "../../create-client-config.ts";
import { shouldAutomaticallyImportBillingCustomers } from "../../import/automatic.ts";
import { resolveBillingWebhookApplicationId } from "../../ingestion/application-id.ts";
import {
  assertAthenaBillingIngestion,
  signingSecretsFromWebhookConfig,
} from "../../ingestion/config.ts";
import {
  type BillingConnectionSigningSecretCache,
  createPostgresBillingWebhookSecretStore,
  rememberConnectionSigningSecret,
  rememberConnectionSigningSecretSet,
  resolveBillingWebhookMasterKey,
  signingSecretsForConnection,
} from "../../ingestion/secrets/index.ts";
import type { AthenaBillingModule } from "../../module.ts";
import {
  assertLocalBillingObservability,
  normalizeAthenaBillingObservability,
} from "../../observability/config.ts";
import { purgeBillingObservability } from "../../observability/retention.ts";
import type { BillingProviderConfigMap } from "../../providers/types.ts";
import {
  DEFAULT_BILLING_PLAN_CHANGE_RECOVERY_SCHEDULE,
  DEFAULT_BILLING_WEBHOOK_RECONCILIATION_SCHEDULE,
  registerEmbeddedBillingScheduler,
} from "../../reconciliation/scheduler.ts";
import type { BillingSqlExecutor } from "../../subject/repository.ts";
import type { AthenaBillingCatalogConfig } from "../../types.ts";
import { isBillingSelfPlanChangeEnabled } from "../../self-enrollment.ts";
import { createPlanChangeRecoveryRuntime } from "../../workflows/plan-change/plan-change-recovery-runtime.ts";
import {
  type AthenaBillingInspectedConnection,
  patchBillingDiagnostics,
} from "../diagnostics.ts";
import { resolveBillingEnvironment } from "../environment.ts";
import { PROCESS_BILLING_INVOCATION } from "../invocation-authority.ts";
import {
  assertLocalBillingRuntimeEnvironment,
  type BillingRuntimeModePreference,
  resolveBillingRuntimeMode,
} from "../resolve-mode.ts";
import {
  configuredBillingConnectionInitKey,
  type MaterializedBillingConnection,
  materializeConfiguredBillingConnections,
  singleFlightConfiguredBillingConnectionMaterialize,
} from "./connections/materialize.ts";
import { attachEventIngressRuntime } from "./ingress/attach.ts";
import { assertBillingProviderRuntimeEnvironment } from "./providers/config.ts";
import { createBillingProviderRegistry } from "./providers/create-registry.ts";
import type { BillingProviderRegistry } from "./providers/registry.ts";
import { createLocalBillingRuntime } from "./runtime.ts";

export const BILLING_BOOTSTRAP_EVENT = {
  connectionMaterialized: "connection-materialized",
  importSchedulerRegistered: "import-scheduler-registered",
  planChangeRecoverySchedulerRegistered:
    "plan-change-recovery-scheduler-registered",
  postgresReady: "postgres-ready",
  providerRegistryReady: "provider-registry-ready",
  runtimeReady: "runtime-ready",
  webhookSchedulerRegistered: "webhook-scheduler-registered",
} as const;

function recordBillingBootstrapEvent(
  events: string[] | undefined,
  event: string
): void {
  events?.push(event);
}

function hasPlanChangeProvider(registry: BillingProviderRegistry): boolean {
  return registry
    .list()
    .some((runtime) => runtime.prices != null && runtime.subscriptions != null);
}

function billingSqlFromInternals(client: {
  billing: AthenaBillingModule;
}): BillingSqlExecutor | undefined {
  const internals = getAthenaClientInternals(client);
  const owned = internals?.postgresRuntime;
  if (!owned) {
    return;
  }
  return {
    async query(sql, params) {
      const result = await owned.query(
        sql,
        params != null && params.length > 0 ? [...params] : undefined
      );
      return { rows: result.rows as Record<string, unknown>[] };
    },
    async transaction<T>(fn: (transaction: BillingSqlExecutor) => Promise<T>) {
      return owned.transaction(async (transactionRuntime) =>
        fn({
          async query(sql, params) {
            const result = await transactionRuntime.query(
              sql,
              params != null && params.length > 0 ? [...params] : undefined
            );
            return { rows: result.rows as Record<string, unknown>[] };
          },
        })
      );
    },
  };
}

export function attachLocalBillingRuntime(
  client: { billing: AthenaBillingModule },
  input: {
    appUrl?: string | null;
    applicationId?: string;
    catalog?: AthenaBillingCatalogConfig;
    configuredProviders?: BillingProviderConfigMap;
    customerImport?: AthenaBillingConfig["import"];
    ingestion?: AthenaBillingConfig["ingestion"];
    mode?: BillingRuntimeModePreference;
    observability?: AthenaBillingConfig["observability"];
    registry?: BillingProviderRegistry;
    selfEnrollment?: AthenaBillingConfig["selfEnrollment"];
    testMode?: boolean;
  }
): void {
  const billingMode = resolveBillingRuntimeMode({
    configuredProviders: input.configuredProviders,
    mode: input.mode,
  });
  if (billingMode !== "local") {
    return;
  }
  const testMode = resolveBillingEnvironment({
    testMode: input.testMode,
  }).testMode;
  const mollieKind = input.configuredProviders?.mollie?.credentialKind;
  const ingestion = assertAthenaBillingIngestion({
    appUrl: input.appUrl,
    credentialKind: mollieKind,
    ingestion: input.ingestion,
    live: testMode === false,
  });
  const observability = normalizeAthenaBillingObservability(
    input.observability
  );
  const existing = getAthenaClientInternals(client);
  const registry =
    input.registry ??
    existing?.billingProviderRegistry ??
    createBillingProviderRegistry(input.configuredProviders, input.catalog);
  const webhookApplicationId = input.applicationId?.trim();
  if (
    ingestion.webhooks.enabled &&
    (webhookApplicationId == null || webhookApplicationId.length === 0)
  ) {
    throw new AthenaConfigurationError(
      "ATHENA_BILLING_APPLICATION_ID_REQUIRED",
      "createClient({ app: { id } }) or client is required when billing webhook management is enabled. app.name is not a webhook ownership id.",
      "billing"
    );
  }
  const bootstrapEvents: string[] = [];
  recordBillingBootstrapEvent(
    bootstrapEvents,
    BILLING_BOOTSTRAP_EVENT.providerRegistryReady
  );
  if (existing) {
    patchBillingDiagnostics(existing, {
      connectionCount: 0,
      phase: "provider-registry",
      startedAt: Date.now(),
    });
  }
  const sql = billingSqlFromInternals(client);
  if (sql != null) {
    recordBillingBootstrapEvent(
      bootstrapEvents,
      BILLING_BOOTSTRAP_EVENT.postgresReady
    );
  }
  const webhookMasterKey = resolveBillingWebhookMasterKey({
    configured: ingestion.webhooks.secretMasterKey,
    databaseUrl: existing ? resolveDatabaseUri(existing.config) : undefined,
  });
  const secretStore =
    sql != null && webhookMasterKey != null
      ? createPostgresBillingWebhookSecretStore({
          masterKey: webhookMasterKey,
          sql,
        })
      : undefined;
  const connectionState: {
    connections: MaterializedBillingConnection[];
    initializationFailed?: boolean;
    initializationMessage?: string;
    initialized: boolean;
    recoveryCoordinatorEnabled: boolean;
  } = {
    connections: [],
    initialized: false,
    recoveryCoordinatorEnabled: false,
  };
  const readiness =
    existing?.runtimeReadiness ?? createAthenaRuntimeReadiness();
  const billingReady = readiness.waitFor("billing");
  if (existing) {
    existing.billingBootstrapEvents = bootstrapEvents;
    existing.billingReady = billingReady;
    existing.runtimeReadiness = readiness;
  }
  const localRuntime = createLocalBillingRuntime({
    applicationId: webhookApplicationId,
    appUrl: input.appUrl,
    configuredProviders: input.configuredProviders,
    connectionState,
    customerImport: input.customerImport,
    ingestion: input.ingestion,
    invocation: PROCESS_BILLING_INVOCATION,
    observability,
    ready: billingReady,
    registry,
    selfEnrollmentEnabled: input.selfEnrollment,
    sql,
    testMode,
  });
  const billing = client.billing;
  billing.payments = localRuntime.payments as AthenaBillingModule["payments"];
  billing.customers = localRuntime.customers;
  billing.refunds = localRuntime.refunds;
  billing.paymentLinks = localRuntime.paymentLinks;
  billing.subscriptions = localRuntime.subscriptions;
  billing.invoices = localRuntime.invoices;
  billing.webhooks = localRuntime.webhooks;
  billing.catalog = {
    prices: localRuntime.prices,
    products: localRuntime.products,
    relations: localRuntime.relations,
  };
  billing.checkout = localRuntime.checkout;
  billing.self = localRuntime.self;
  billing.admin = localRuntime.admin;
  billing.health = localRuntime.health;
  billing.getCapabilities =
    localRuntime.getCapabilities as AthenaBillingModule["getCapabilities"];
  const signingSecretCache: BillingConnectionSigningSecretCache = new Map();
  const configuredSecrets = signingSecretsFromWebhookConfig(ingestion.webhooks);
  const rememberSigningSecret = (entry: {
    connectionId: string;
    secret: string;
  }): void => {
    rememberConnectionSigningSecret(
      signingSecretCache,
      entry.connectionId,
      entry.secret
    );
  };
  const resolveSigningSecrets = (connectionId: string): readonly string[] =>
    signingSecretsForConnection(signingSecretCache, connectionId);
  if (existing) {
    existing.billingRuntime = localRuntime;
    existing.billingProviderRegistry = registry;
    patchBillingDiagnostics(existing, {
      phase: "attaching-ingress",
    });
    attachEventIngressRuntime(existing, {
      configuredProviders: input.configuredProviders,
      registry,
      resolveSigningSecrets,
      testMode,
    });
  }
  void (async () => {
    try {
      patchBillingDiagnostics(existing, {
        phase: "materializing-connections",
      });
      if (
        sql != null &&
        webhookApplicationId != null &&
        webhookApplicationId.length > 0
      ) {
        const materialized =
          await singleFlightConfiguredBillingConnectionMaterialize(
            configuredBillingConnectionInitKey({
              applicationId: webhookApplicationId,
              testMode,
            }),
            () =>
              materializeConfiguredBillingConnections({
                applicationId: webhookApplicationId,
                appUrl: input.appUrl,
                configuredProviders: input.configuredProviders,
                publicBaseUrl: ingestion.webhooks.publicBaseUrl,
                sql,
                testMode,
              })
          );
        connectionState.connections = [...materialized.connections];
        const inspected: AthenaBillingInspectedConnection[] =
          connectionState.connections.map((row) => ({
            accountReference: row.accountReference,
            credentialReference: row.credentialReference,
            id: row.id,
            provider: row.provider,
            ...(row.classicWebhookUrl
              ? { classicWebhookUrl: row.classicWebhookUrl }
              : {}),
            ...(row.eventsWebhookUrl
              ? { eventsWebhookUrl: row.eventsWebhookUrl }
              : {}),
          }));
        const classicWebhookUrlsByConnectionId = Object.fromEntries(
          connectionState.connections
            .filter(
              (
                row
              ): row is MaterializedBillingConnection & {
                classicWebhookUrl: string;
              } => row.classicWebhookUrl != null
            )
            .map((row) => [row.id, row.classicWebhookUrl])
        );
        const firstConnection = connectionState.connections[0];
        registry.ingress = {
          ...(firstConnection?.classicWebhookUrl
            ? { classicWebhookUrl: firstConnection.classicWebhookUrl }
            : {}),
          ...(Object.keys(classicWebhookUrlsByConnectionId).length > 0
            ? { classicWebhookUrlsByConnectionId }
            : {}),
        };
        if (existing) {
          existing.billingMaterializedConnections = inspected;
          patchBillingDiagnostics(existing, {
            connectionCount: inspected.length,
          });
        }
        for (const connection of connectionState.connections) {
          if (secretStore) {
            try {
              const stored = await secretStore.resolve(connection.id);
              rememberConnectionSigningSecretSet(
                signingSecretCache,
                connection.id,
                stored
              );
            } catch (error) {
              console.warn(
                "[athena-billing] webhook signing secrets could not be loaded; apply embedded billing migrations through 0015",
                {
                  connectionId: connection.id,
                  error: error instanceof Error ? error.message : String(error),
                }
              );
            }
          }
          if (connectionState.connections.length === 1) {
            rememberConnectionSigningSecretSet(
              signingSecretCache,
              connection.id,
              configuredSecrets
            );
          }
        }
      }
      recordBillingBootstrapEvent(
        bootstrapEvents,
        BILLING_BOOTSTRAP_EVENT.connectionMaterialized
      );
      connectionState.initialized = true;
      const recoveryScheduler = registerEmbeddedBillingScheduler({
        enabled:
          sql != null &&
          isBillingSelfPlanChangeEnabled(input.selfEnrollment) &&
          connectionState.connections.length > 0 &&
          hasPlanChangeProvider(registry),
        run: async () => {
          await billingReady;
          const schedulerSql = billingSqlFromInternals(client);
          if (!schedulerSql) {
            return;
          }
          const recovery = createPlanChangeRecoveryRuntime({
            batchSize: 25,
            configuredProviders: input.configuredProviders,
            diagnostics: (event) => {
              console.info("[athena-billing] plan change recovery", event);
            },
            registry,
            sql: schedulerSql,
            testMode,
          });
          const result = await recovery.run();
          patchBillingDiagnostics(existing, {
            lastRecoveryBatch: {
              attentionRequired: result.attentionRequired,
              completed: result.completed,
              failed: result.failed,
              retried: result.retried,
              scanned: result.scanned,
            },
            ...(result.recoverableOperationBacklog === undefined
              ? {}
              : {
                  recoverableOperationBacklog:
                    result.recoverableOperationBacklog,
                }),
          });
        },
        schedule: DEFAULT_BILLING_PLAN_CHANGE_RECOVERY_SCHEDULE,
      });
      connectionState.recoveryCoordinatorEnabled = recoveryScheduler != null;
      if (existing) {
        existing.billingPlanChangeRecoveryScheduler =
          recoveryScheduler ?? undefined;
        patchBillingDiagnostics(existing, {
          recoveryCoordinatorEnabled: connectionState.recoveryCoordinatorEnabled,
        });
      }
      if (recoveryScheduler) {
        recordBillingBootstrapEvent(
          bootstrapEvents,
          BILLING_BOOTSTRAP_EVENT.planChangeRecoverySchedulerRegistered
        );
      }
      recordBillingBootstrapEvent(
        bootstrapEvents,
        BILLING_BOOTSTRAP_EVENT.runtimeReady
      );
      patchBillingDiagnostics(existing, {
        phase: "registering-schedulers",
      });
      registerEmbeddedBillingScheduler({
        enabled: ingestion.webhooks.enabled,
        execution: ingestion.webhooks.execution,
        run: async (trigger) => {
          await billingReady;
          const schedulerSql = billingSqlFromInternals(client);
          if (!schedulerSql || webhookApplicationId == null) {
            return;
          }
          const { runAutomaticBillingWebhookReconcileIfEnabled } = await import(
            "../../ingestion/automatic.ts"
          );
          await runAutomaticBillingWebhookReconcileIfEnabled({
            applicationId: webhookApplicationId,
            appUrl: input.appUrl,
            configuredProviders: input.configuredProviders,
            observability,
            registry,
            rememberSigningSecret,
            secretStore,
            sql: schedulerSql,
            testMode,
            trigger,
            webhooks: ingestion.webhooks,
          });
        },
        schedule: DEFAULT_BILLING_WEBHOOK_RECONCILIATION_SCHEDULE,
      });
      recordBillingBootstrapEvent(
        bootstrapEvents,
        BILLING_BOOTSTRAP_EVENT.webhookSchedulerRegistered
      );
      registerEmbeddedBillingScheduler({
        enabled: shouldAutomaticallyImportBillingCustomers({
          enabled: input.customerImport?.customers?.enabled === true,
          mode: input.customerImport?.customers?.mode,
        }),
        execution: input.customerImport?.customers?.execution,
        run: async (trigger) => {
          await billingReady;
          const schedulerSql = billingSqlFromInternals(client);
          if (!schedulerSql) {
            return;
          }
          const { runAutomaticBillingCustomerImportIfEnabled } = await import(
            "../../import/assemble.ts"
          );
          await runAutomaticBillingCustomerImportIfEnabled({
            configuredProviders: input.configuredProviders,
            customerImport: input.customerImport,
            observability,
            sql: schedulerSql,
            testMode,
            trigger,
          });
          await purgeBillingObservability({
            observability,
            sql: schedulerSql,
          });
        },
        schedule: input.customerImport?.customers?.schedule,
      });
      recordBillingBootstrapEvent(
        bootstrapEvents,
        BILLING_BOOTSTRAP_EVENT.importSchedulerRegistered
      );
      patchBillingDiagnostics(existing, {
        connectionCount:
          existing?.billingMaterializedConnections?.length ??
          connectionState.connections.length,
        phase: "ready",
        readyAt: Date.now(),
      });
      readiness.ready("billing");
    } catch (error) {
      console.error(
        "[athena-billing] configured billing connections failed to materialize",
        {
          error: error instanceof Error ? error.message : String(error),
        }
      );
      connectionState.initializationFailed = true;
      connectionState.initializationMessage =
        error instanceof Error ? error.message : String(error);
      const raw =
        error instanceof Error && error.message.trim().length > 0
          ? error.message.trim()
          : "billing initialization failed";
      const sanitized = /access_|live_|test_|sk_|secret|password|token/i.test(
        raw
      )
        ? "billing initialization failed"
        : raw.length > 240
          ? `${raw.slice(0, 237)}...`
          : raw;
      patchBillingDiagnostics(existing, {
        failure: {
          message: sanitized,
          stage: "materializing-connections",
        },
        phase: "failed",
      });
      readiness.fail("billing", error);
    }
  })();
}

/** Core createClient plus local Billing overlay. Used by Cloudflare façades. */
export function createClientWithAttachedLocalBilling<
  TModels extends AthenaClientConfig["models"] = AthenaClientConfig["models"],
>(config: AthenaClientConfig<TModels>): AthenaClient<TModels> {
  assertDataLifecycleConfig(config);
  assertBillingProviderRuntimeEnvironment(config.billing?.providers);
  const factory = createClientWithNormalizer as unknown as (
    input: unknown,
    normalizer: (c: unknown) => unknown
  ) => unknown;
  const normalize = normalizeUniversalCreateClientConfig as unknown as (
    c: unknown
  ) => unknown;
  const client = factory(config, normalize) as AthenaClient<TModels>;
  assertLocalAuthHooks(athenaAuthConfig(config.auth));
  assertLocalAuthObservability(athenaAuthConfig(config.auth));
  assertLocalBillingObservability(config.billing);
  attachLocalBillingRuntime(client, {
    applicationId: resolveBillingWebhookApplicationId({
      app: config.app,
      client: config.client,
    }),
    appUrl: config.app?.url,
    catalog: config.billing?.catalog,
    configuredProviders: config.billing?.providers,
    customerImport: config.billing?.import,
    ingestion: config.billing?.ingestion,
    mode: config.billing?.mode,
    observability: config.billing?.observability,
    selfEnrollment: config.billing?.selfEnrollment,
    testMode: config.billing?.testMode,
  });
  assertLocalBillingRuntimeEnvironment({
    configuredProviders: config.billing?.providers,
    localMaterialized: true,
    mode: config.billing?.mode,
  });
  return client;
}
